import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { claudeTokens } from '@/styles/claudeTokens';

/**
 * 풀투리프레시 — 페이지 맨 위에서 아래로 당기면 헤더는 그대로 두고 본문(#app-content)만 내려가며
 * 헤더 아래 빈 자리에 명치사 로고가 드러난다. 임계치(PULL_THRESHOLD)를 넘겨 놓으면 현재 화면을 새로고침한다.
 *
 * 자연스러움을 위해 지킨 것:
 * - 손가락 이동마다 React 리렌더 없이 DOM 스타일만 rAF 로 갱신한다(끊김 방지).
 * - 당김 거리는 고무줄처럼 갈수록 덜 늘어난다(지수 감쇠) — 선형 0.5 배율은 뻣뻣하게 느껴진다.
 * - 놓았을 때는 스프링 느낌의 이징으로 되돌아가고, 새로고침 중에는 로고 아래 링이 돈다.
 * - 새로고침은 문서 reload 가 아니라 현재 라우트 remount(소프트) — 흰 화면 깜박임이 없다.
 * - 터치 입력에서만 동작한다(마우스 휠 무시). 스크롤이 0 이 아니거나 위로 미는 제스처는 손대지 않는다.
 */

/** 본문 컨테이너 id — App.tsx 의 main Container 에 붙어 있다 */
export const PULL_CONTENT_ID: string = 'app-content';
/** 소프트 새로고침 이벤트 — App.tsx 가 받아 현재 라우트를 remount 한다 (문서 reload 로 인한 흰 화면 깜박임 방지) */
export const SOFT_REFRESH_EVENT: string = 'myungchisa:soft-refresh';
/** 새로고침 링을 보여 주는 최소 시간 — 너무 짧으면 "된 건지" 모르고, 길면 답답하다 */
const REFRESH_SHOW_MS: number = 700;
const PULL_THRESHOLD: number = 64;
const MAX_PULL: number = 110;
const REFRESH_HOLD: number = 64;
const RELEASE_EASING: string = 'cubic-bezier(0.22, 1, 0.36, 1)';
const RELEASE_MS: number = 320;
/** 손가락을 떼기 전 이 거리 이상 움직여야 "당기는 중"으로 본다 — 탭을 당김으로 오인하지 않게 */
const START_SLOP: number = 6;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: ${claudeTokens.color.bg};
    overflow: hidden;
    pointer-events: none;
    visibility: hidden;
`;

const Brand = styled.div`
    display: flex;
    align-items: center;
    font-family: 'Pretendard Variable', Pretendard, system-ui, sans-serif;
    font-size: 1.45rem;
    font-weight: 800;
    color: ${claudeTokens.color.text1};
    letter-spacing: -0.03em;
    line-height: 1;
    user-select: none;
    will-change: transform, opacity;

    &::after {
        content: '';
        display: inline-block;
        width: 0.32em;
        height: 0.32em;
        background: ${claudeTokens.color.accent};
        border-radius: 50%;
        margin-left: 0.08em;
        vertical-align: baseline;
    }
`;

const Ring = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid ${claudeTokens.color.accentSoft};
    border-top-color: ${claudeTokens.color.accent};
    opacity: 0;
    will-change: transform, opacity;

    &[data-spinning='true'] {
        opacity: 1;
        animation: ${spin} 0.8s linear infinite;
    }
`;

const isTouchDevice = (): boolean => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

/** sticky 헤더의 아래 끝 — 로고 레이어는 여기서부터 시작한다 (헤더가 없으면 0) */
const headerBottom = (): number => {
    const header: Element | null = document.querySelector('header');
    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
};

/** 터치가 가로로 넘기는 목록(오늘의 채용 카드 등) 안에서 시작했는지 — 이런 곳은 세로가 확실히 클 때만 당김으로 본다 */
const inHorizontalScroller = (target: EventTarget | null): boolean => {
    let el: Element | null = target instanceof Element ? target : null;
    while (el && el !== document.body) {
        if (el.scrollWidth > el.clientWidth + 1) {
            const overflowX: string = window.getComputedStyle(el).overflowX;
            if (overflowX === 'auto' || overflowX === 'scroll') return true;
        }
        el = el.parentElement;
    }
    return false;
};

/** 고무줄 감쇠 — 손가락 이동량(dy)이 커질수록 덜 늘어난다 */
const rubberBand = (dy: number): number => MAX_PULL * (1 - Math.exp(-dy / MAX_PULL));

const PullToRefresh = () => {
    const backdropRef = useRef<HTMLDivElement | null>(null);
    const brandRef = useRef<HTMLDivElement | null>(null);
    const ringRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isTouchDevice()) return undefined;

        let startY: number | null = null;
        let startX: number = 0;
        /** 가로 넘김 목록 안에서 시작한 터치 — 세로 이동이 가로의 2배를 넘어야 당김으로 본다 */
        let inScroller: boolean = false;
        let pulling: boolean = false;
        let refreshing: boolean = false;
        let offset: number = 0;
        let frame: number = 0;
        let pendingOffset: number = 0;

        const content = (): HTMLElement | null => document.getElementById(PULL_CONTENT_ID);

        const paint = (value: number, animate: boolean): void => {
            const target: HTMLElement | null = content();
            const backdrop: HTMLDivElement | null = backdropRef.current;
            const brand: HTMLDivElement | null = brandRef.current;
            const ring: HTMLDivElement | null = ringRef.current;
            const transition: string = animate ? `${RELEASE_MS}ms ${RELEASE_EASING}` : 'none';
            if (target) {
                target.style.transition = animate ? `transform ${transition}` : 'none';
                target.style.transform = value > 0 ? `translate3d(0, ${value}px, 0)` : '';
            }
            if (backdrop) {
                backdrop.style.transition = animate ? `height ${transition}` : 'none';
                backdrop.style.height = `${value}px`;
                backdrop.style.visibility = value > 0 ? 'visible' : 'hidden';
            }
            if (brand) {
                const progress: number = Math.min(1, value / PULL_THRESHOLD);
                brand.style.opacity = `${Math.min(1, 0.2 + progress * 0.8)}`;
                brand.style.transform = `scale(${0.8 + progress * 0.2}) translateY(${(1 - progress) * 6}px)`;
            }
            if (ring && !refreshing) {
                // 임계치에 가까워질수록 링이 서서히 나타나 "놓으면 된다"는 신호를 준다
                const progress: number = Math.min(1, value / PULL_THRESHOLD);
                ring.style.opacity = `${Math.max(0, (progress - 0.6) / 0.4)}`;
                ring.style.transform = `rotate(${progress * 270}deg)`;
            }
        };

        const schedule = (value: number): void => {
            pendingOffset = value;
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                offset = pendingOffset;
                paint(offset, false);
            });
        };

        const onTouchStart = (event: TouchEvent): void => {
            if (refreshing || event.touches.length !== 1) return;
            // 메뉴 등이 body 스크롤을 잠근 동안(scrollY 가 항상 0)에는 당김을 새로고침으로 오인하지 않는다
            if (document.body.dataset.scrollLock === 'true') return;
            // 글쓰기 화면(속닥속닥·전문가 라운지 글쓰기·수정)은 당겨서 새로고침을 쓰지 않는다 — 키보드가 뜬 채 글을 아래로 끌면 본문 전체가
            // 내려가 위에 빈 영역이 생기고 도구 줄이 떨어졌다 돌아왔으며(2026-10-07 실기기 녹화), 끝까지 당기면 화면을 다시 그려 쓰던 글이 사라진다
            if (document.body.dataset.writeScreen === 'true') return;
            // 페이지 맨 위에서 시작한 터치만 후보로 본다. 문서 대신 #root 가 스크롤되는 화면(아이폰 대화방 — 문서는 늘 0)은 #root 위치를 본다
            if (window.scrollY > 0 || (document.getElementById('root')?.scrollTop ?? 0) > 0) return;
            startY = event.touches[0].clientY;
            startX = event.touches[0].clientX;
            inScroller = inHorizontalScroller(event.target);
            pulling = false;
            if (backdropRef.current) backdropRef.current.style.top = `${headerBottom()}px`;
        };

        const onTouchMove = (event: TouchEvent): void => {
            if (refreshing || startY === null) return;
            // 가장자리 스와이프 뒤로가기(SwipeBack)가 이 터치를 가져갔으면 당김을 접고 물러난다 — 대각선으로 끌 때 두 제스처가 같이 움직이지 않게
            if (document.body.dataset.swipeBack === 'true') {
                startY = null;
                if (pulling) {
                    pulling = false;
                    offset = 0;
                    pendingOffset = 0;
                    paint(0, false);
                }
                return;
            }
            const dy: number = event.touches[0].clientY - startY;
            if (!pulling) {
                // 가로로 넘기는 손짓이면 당김 후보에서 뺀다. 예전엔 대각선 손짓도 당김으로 잡아 preventDefault 로 가로 스크롤을 끊어,
                // 오늘의 채용 카드가 스냅 없이 중간에 멈췄다(2026-11-03 실기기). 가로 목록 안에서는 세로가 가로의 2배를 넘어야 한다
                const dx: number = Math.abs(event.touches[0].clientX - startX);
                if (dx > START_SLOP && dx * (inScroller ? 2 : 1) >= Math.abs(dy)) {
                    startY = null;
                    return;
                }
                if (dy <= START_SLOP) {
                    if (dy < 0) startY = null;
                    return;
                }
                // 세로로 넘었어도 가로 목록 안에서 대각선이면 아직 정하지 않는다(가로 넘김일 수 있다)
                if (inScroller && dy < dx * 2) return;
                if (window.scrollY > 0 || (document.getElementById('root')?.scrollTop ?? 0) > 0) {
                    startY = null;
                    return;
                }
                pulling = true;
            }
            const next: number = dy > 0 ? rubberBand(dy) : 0;
            schedule(next);
            // 당기는 동안 브라우저의 자체 스크롤/바운스가 끼어들지 않게 막는다 (passive: false 필요)
            if (event.cancelable) event.preventDefault();
        };

        const finish = (): void => {
            if (startY === null) return;
            startY = null;
            if (!pulling) return;
            pulling = false;
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = 0;
                offset = pendingOffset;
            }
            if (offset >= PULL_THRESHOLD) {
                refreshing = true;
                paint(REFRESH_HOLD, true);
                if (ringRef.current) {
                    ringRef.current.style.transform = '';
                    ringRef.current.setAttribute('data-spinning', 'true');
                }
                // 문서 reload 대신 현재 라우트만 다시 그린다(App.tsx 의 refreshKey). 링은 잠깐 보여 준 뒤 원위치로 접는다.
                window.dispatchEvent(new Event(SOFT_REFRESH_EVENT));
                window.setTimeout(() => {
                    refreshing = false;
                    offset = 0;
                    if (ringRef.current) {
                        ringRef.current.setAttribute('data-spinning', 'false');
                        ringRef.current.style.opacity = '0';
                    }
                    paint(0, true);
                }, REFRESH_SHOW_MS);
                return;
            }
            offset = 0;
            paint(0, true);
        };

        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', finish, { passive: true });
        document.addEventListener('touchcancel', finish, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', finish);
            document.removeEventListener('touchcancel', finish);
            if (frame) window.cancelAnimationFrame(frame);
            paint(0, false);
        };
    }, []);

    if (!isTouchDevice()) return null;
    return (
        <Backdrop ref={backdropRef} aria-hidden="true">
            <Brand ref={brandRef}>명치사</Brand>
            <Ring ref={ringRef} data-spinning="false" />
        </Backdrop>
    );
};

export default PullToRefresh;
